import React, { useState, useRef, useEffect } from 'react';
import { Play, Clock, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import LazyImage from './LazyImage';
import VideoPlayer from './VideoPlayer';

const LazyMovieCard = ({ 
  video, 
  interactionMode = "modal", 
  onPlay, 
  onExpand, 
  isExpanded = false,
  className = "",
  shouldLoadImmediately = false // New prop to bypass lazy loading for above-the-fold content
}) => {
  const [isInView, setIsInView] = useState(shouldLoadImmediately); // Start as true if should load immediately
  const cardRef = useRef(null);

  useEffect(() => {
    // If should load immediately, skip intersection observer
    if (shouldLoadImmediately) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px' // Start loading 100px before the card comes into view
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [shouldLoadImmediately]);

  const handleClick = () => {
    switch (interactionMode) {
      case "expand":
        onExpand?.(video);
        break;
      case "modal":
        onPlay?.(video);
        break;
      case "page":
        window.location.href = `/movie/${video.id}`;
        break;
      default:
        // Default behavior: always open full-screen player
        onPlay?.(video);
    }
  };

  // Show placeholder until in view
  if (!isInView) {
    return (
      <div ref={cardRef} className={`relative ${className}`}>
        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800 animate-pulse">
          <div className="w-full h-full bg-gray-700"></div>
        </div>
        <div className="mt-2 h-4 bg-gray-700 rounded animate-pulse"></div>
        <div className="mt-1 h-3 bg-gray-700 rounded animate-pulse w-3/4"></div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`relative group cursor-pointer no-mobile-hover ${className} transition-transform duration-200 hover:scale-105`}
      onClick={handleClick}
    >
      {/* Movie Poster */}
      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800">
        <LazyImage
          src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
          alt={video.title}
          className="w-full h-full"
          videoId={video.id}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transition-transform duration-200 hover:scale-110 active:scale-95">
              <Play className="w-8 h-8 text-white" fill="white" />
            </div>
          </div>
        </div>

        {/* Bottom Info Overlay - Hidden on mobile */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 hidden md:block">
          <div className="flex items-center justify-between text-white text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{video.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>{video.views}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Movie Title */}
      <h3 className="mt-2 text-white font-medium text-sm md:text-base line-clamp-2">
        {video.title}
      </h3>

      {/* Expandable Content */}
      {isExpanded && interactionMode === "expand" && (
        <div className="overflow-hidden transition-all duration-300 ease-in-out">
          <div className="mt-4 space-y-4">
            <VideoPlayer
              videoId={video.id}
              title={video.title}
              onPlay={onPlay}
              className="w-full"
            />
            <div className="flex items-center justify-between">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand?.(video);
                }}
                className="flex items-center gap-2 text-primary-purple hover:text-primary-gold transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expand Button for expand mode */}
      {interactionMode === "expand" && !isExpanded && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onExpand?.(video);
          }}
          className="mt-2 flex items-center gap-2 text-primary-purple hover:text-primary-gold transition-colors text-sm"
        >
          <ChevronDown className="w-4 h-4" />
          <span>Expand</span>
        </button>
      )}
    </div>
  );
};

export default LazyMovieCard;
